import { useEffect, useState } from 'react';
import { useNavigate, Routes, Route, Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2, LogOut, Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_API_URL;


const AdminDashboard = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    image_url: '',
    wattage: '',
    material: '',
    application: '',
    stock: '10'
  });

  useEffect(() => {
    const token = localStorage.getItem('avex_admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/products`),
        axios.get(`${BACKEND_URL}/api/categories`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('avex_admin_token');
    localStorage.removeItem('avex_admin_username');
    navigate('/admin/login');
  };

  const openModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        category_id: product.category_id.toString(),
        image_url: product.image_url,
        wattage: product.wattage || '',
        material: product.material || '',
        application: product.application || '',
        stock: product.stock.toString()
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        category_id: '',
        image_url: '',
        wattage: '',
        material: '',
        application: '',
        stock: '10'
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        category_id: parseInt(formData.category_id),
        stock: parseInt(formData.stock)
      };

      if (editingProduct) {
        await axios.put(`${BACKEND_URL}/api/admin/products/${editingProduct.id}`, data);
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/products`, data);
      }

      fetchData();
      closeModal();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product. Please try again.');
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`${BACKEND_URL}/api/admin/products/${productId}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Error deleting product.');
      }
    }
  };

  const username = localStorage.getItem('avex_admin_username');

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Admin Header */}
      <header className="bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img
  src="/assets/avex-logo.png"
  alt="Avex Enterprises"
  className="h-12"
/>

              <div>
                <h1 className="font-heading text-xl font-bold" data-testid="admin-dashboard-title">Admin Dashboard</h1>
                <p className="text-sm text-white/60">Welcome, {username}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 border border-[#E5E5E5]">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#BFA15F] rounded-full flex items-center justify-center">
                <Package className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="total-products-count">{products.length}</p>
                <p className="text-sm text-[#888888]">Total Products</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 border border-[#E5E5E5]">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#BFA15F] rounded-full flex items-center justify-center">
                <Package className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{categories.length}</p>
                <p className="text-sm text-[#888888]">Categories</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 border border-[#E5E5E5]">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#BFA15F] rounded-full flex items-center justify-center">
                <Package className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {products.reduce((sum, p) => sum + p.stock, 0)}
                </p>
                <p className="text-sm text-[#888888]">Total Stock</p>
              </div>
            </div>
          </div>
        </div>

        {/* Products Management */}
        <div className="bg-white border border-[#E5E5E5]">
          <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
            <h2 className="font-heading text-2xl font-bold">Product Management</h2>
            <button
              onClick={() => openModal()}
              className="bg-[#BFA15F] text-white hover:bg-[#A68B4F] px-6 py-2 uppercase tracking-wider text-sm font-medium transition-all duration-300 flex items-center gap-2"
              data-testid="add-product-btn"
            >
              <Plus className="h-4 w-4" /> Add Product
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F9F9F7]">
                <tr>
                  <th className="text-left p-4 font-semibold text-sm">Image</th>
                  <th className="text-left p-4 font-semibold text-sm">Name</th>
                  <th className="text-left p-4 font-semibold text-sm">Category</th>
                  <th className="text-left p-4 font-semibold text-sm">Price</th>
                  <th className="text-left p-4 font-semibold text-sm">Stock</th>
                  <th className="text-left p-4 font-semibold text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="border-b border-[#E5E5E5]" data-testid={`admin-product-row-${product.id}`}>
                    <td className="p-4">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-16 h-16 object-cover bg-[#F0F0ED]"
                      />
                    </td>
                    <td className="p-4">
                      <div className="font-medium">{product.name}</div>
                      <div className="text-sm text-[#888888] line-clamp-1">{product.description}</div>
                    </td>
                    <td className="p-4 text-sm">{product.category_name}</td>
                    <td className="p-4 font-semibold">NPR {product.price.toLocaleString()}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs ${
                        product.stock < 5 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                      }`}>
                        {product.stock}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openModal(product)}
                          className="text-[#BFA15F] hover:text-[#A68B4F] transition-colors"
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="text-red-600 hover:text-red-700 transition-colors"
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="product-modal">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-heading text-xl font-bold">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h3>
              <button onClick={closeModal} className="text-[#888888] hover:text-[#1A1A1A]" data-testid="close-modal">
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Product Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-[#E5E5E5] focus:border-[#BFA15F] px-4 py-2 focus:outline-none transition-colors"
                  required
                  data-testid="product-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-[#E5E5E5] focus:border-[#BFA15F] px-4 py-2 focus:outline-none transition-colors"
                  rows="3"
                  required
                  data-testid="product-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Price (NPR) *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full border border-[#E5E5E5] focus:border-[#BFA15F] px-4 py-2 focus:outline-none transition-colors"
                    required
                    data-testid="product-price-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Category *</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className="w-full border border-[#E5E5E5] focus:border-[#BFA15F] px-4 py-2 focus:outline-none transition-colors"
                    required
                    data-testid="product-category-input"
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Image URL *</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full border border-[#E5E5E5] focus:border-[#BFA15F] px-4 py-2 focus:outline-none transition-colors"
                  required
                  data-testid="product-image-input"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Wattage</label>
                  <input
                    type="text"
                    value={formData.wattage}
                    onChange={(e) => setFormData({ ...formData, wattage: e.target.value })}
                    className="w-full border border-[#E5E5E5] focus:border-[#BFA15F] px-4 py-2 focus:outline-none transition-colors"
                    placeholder="e.g., 40W LED"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Material</label>
                  <input
                    type="text"
                    value={formData.material}
                    onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                    className="w-full border border-[#E5E5E5] focus:border-[#BFA15F] px-4 py-2 focus:outline-none transition-colors"
                    placeholder="e.g., Metal, Glass"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Stock *</label>
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    className="w-full border border-[#E5E5E5] focus:border-[#BFA15F] px-4 py-2 focus:outline-none transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Application</label>
                <input
                  type="text"
                  value={formData.application}
                  onChange={(e) => setFormData({ ...formData, application: e.target.value })}
                  className="w-full border border-[#E5E5E5] focus:border-[#BFA15F] px-4 py-2 focus:outline-none transition-colors"
                  placeholder="e.g., Living Room, Office"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-[#BFA15F] text-white hover:bg-[#A68B4F] py-3 uppercase tracking-widest text-sm font-medium transition-all duration-300"
                  data-testid="save-product-btn"
                >
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white py-3 uppercase tracking-widest text-sm font-medium transition-all duration-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;